"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useUiStore } from "@/stores/uiStore";
import { useFiltersStore } from "@/stores/filtersStore";
import KurboButton from "@/components/KurboButton";
import { sortSelecton } from "@/lib/shared/constants";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
import { useSearchTips } from "@/hooks/useSearchTips";
import SearchTipsModal from "@/components/SearchTipsModal";

import { Flex, Button, Tooltip, Box as MantineBox, Text as MantineText } from "@mantine/core";
import { IconAdjustmentsHorizontal, IconArrowsSort } from "@tabler/icons-react";

import styles from "./styles.module.scss";

const SCROLL_DELTA = 12;
const TOP_REVEAL_OFFSET = 24;

const MobileFilterMenu = ({ hideFilterBar = false }: { hideFilterBar?: boolean }) => {
  const filterPaneOpen = useUiStore((state) => state.filterPaneOpen);
  const itemModalOpen = useUiStore((state) => state.itemModalOpen);
  const favoritesModalOpen = useUiStore((state) => state.favoritesModalOpen);
  const { setFilterPaneOpen, setItemModalOpen } = useUiStore((state) => state.actions);
  const { isOpen: tipsOpen, openIfUnseen: openTipsIfUnseen, close: closeTips } = useSearchTips();
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const searchText = useFiltersStore((state) => state.searchText);
  const excludeRelists = useFiltersStore((state) => state.excludeRelists);
  const excludeAuctions = useFiltersStore((state) => state.excludeAuctions);
  const updateActiveFilter = useFiltersStore((state) => state.actions.updateActiveFilter);

  const filterCount = useMemo(() => {
    const selectedCount = Object.entries(activeFilters).reduce((count, [key, value]) => {
      if (["id", "available", "sort", "currency"].includes(key) || !Array.isArray(value)) {
        return count;
      }

      if (key === "price") {
        return (
          count +
          value.filter((option) => String((option as FilterOption)?.value) !== "0,Infinity").length
        );
      }

      return count + value.length;
    }, 0);

    return (
      selectedCount + (searchText ? 1 : 0) + (excludeRelists ? 1 : 0) + (excludeAuctions ? 1 : 0)
    );
  }, [activeFilters, excludeAuctions, excludeRelists, searchText]);

  const sortLabel = String(activeFilters.sort?.[0]?.label || "Newest");
  const sortValue = String(activeFilters.sort?.[0]?.value || "newest");

  const handleFilterButtonClick = () => {
    if (!filterPaneOpen) {
      openTipsIfUnseen();
    }
    setFilterPaneOpen(!filterPaneOpen);
    setItemModalOpen(false);
  };

  const handleSortToggle = () => {
    const pairedValue =
      sortValue === "newest"
        ? "oldest"
        : sortValue === "oldest"
          ? "newest"
          : sortValue === "lowest"
            ? "highest"
            : "lowest";
    const option = sortSelecton.find((item) => item.value === pairedValue);

    if (option) {
      updateActiveFilter("sort", [{ ...option, state: "include" }]);
    }
  };

  const pathname = usePathname();
  const [isHidden, setIsHidden] = useState(false);
  const lastDirectionChangeYRef = useRef(0);
  const prevSlideInOpenRef = useRef(false);

  useEffect(() => {
    const slideInOpen = itemModalOpen || favoritesModalOpen;
    if (prevSlideInOpenRef.current && !slideInOpen) {
      setIsHidden(false);
      lastDirectionChangeYRef.current = window.scrollY;
    }
    prevSlideInOpenRef.current = slideInOpen;
  }, [itemModalOpen, favoritesModalOpen]);

  useEffect(() => {
    if (!pathname.includes("search")) return;

    lastDirectionChangeYRef.current = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;

      if (currentY <= TOP_REVEAL_OFFSET) {
        setIsHidden(false);
        lastDirectionChangeYRef.current = currentY;
        return;
      }

      const delta = currentY - lastDirectionChangeYRef.current;
      if (Math.abs(delta) < SCROLL_DELTA) return;

      setIsHidden(delta > 0);
      lastDirectionChangeYRef.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  return (
    <>
      <SearchTipsModal opened={tipsOpen} onClose={closeTips} />
      {!hideFilterBar && (
        <MantineBox className={styles.searchFilter} data-hide={isHidden ? "true" : "false"}>
          <Flex
            w="100%"
            align="center"
            justify="space-between"
            px={{ base: 0, md: "sm" }}
            className={styles.toolbarRow}
          >
            <Flex gap="4px" align="center" className={styles.leftButtons}>
              <Button
                className={styles.filterButton}
                data-active={filterPaneOpen ? "true" : "false"}
                onClick={handleFilterButtonClick}
                variant="unstyled"
                aria-expanded={filterPaneOpen}
                aria-controls="filter-menu"
              >
                <IconAdjustmentsHorizontal size={18} stroke={2} aria-hidden="true" />
                <MantineText span className={styles.filterLabel}>
                  Filters{filterCount > 0 ? ` · ${filterCount}` : ""}
                </MantineText>
              </Button>
              <Button
                type="button"
                variant="unstyled"
                className={styles.sortButton}
                onClick={handleSortToggle}
                aria-label={`Change sort. Currently ${sortLabel}`}
                title={`Change sort · ${sortLabel}`}
              >
                <IconArrowsSort size={16} stroke={1.8} aria-hidden="true" />
                <MantineText span className={styles.sortLabel}>
                  {sortLabel}
                </MantineText>
              </Button>
            </Flex>
            {TOKEN_UI_ENABLED && (
              <Tooltip
                label="KURBO is KURB's real-time search layer. It surfaces listings added in the last hour that match your filters, before the crowd finds them."
                multiline
                maw={280}
                offset={8}
                openDelay={350}
                position="bottom"
                withArrow
                zIndex={10000}
              >
                <MantineBox className={styles.kurboToolbarButton}>
                  <KurboButton variant="toolbar" />
                </MantineBox>
              </Tooltip>
            )}
          </Flex>
        </MantineBox>
      )}
    </>
  );
};

export default MobileFilterMenu;
